import { Grid, List, ListItem, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { getPartnerId, getServicingOpCo } from '../../../app/store/combinedSelectors';
import { updateStageState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { continueBtn, goToClaimsBtn } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { IconStateType, OpCo, PartnerId, ProviderInfo } from '../../../types/type';
import { getNextView, getPrettyPhoneNumber, getStagesInOrder, postMessageToParentApp } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ConfirmationHeader from '../../shared/ConfirmationHeader/ConfirmationHeader';
import ContactCard from '../../shared/ContactCard/ContactCard';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';
interface SelectedProvider {
  provider: ProviderInfo;
}

export default function SingleServiceConfirmation() {
  const theme = useTheme();
  const { state } = useLocation();
  const { provider } = state as SelectedProvider;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);
  const currentStage = availableProgressStages.filter((stage) => stage.viewActive === true);
  const opco = useSelector(getServicingOpCo);
  const partnerId = useSelector(getPartnerId);

  // Get the rest of stages excluding the current stage. Find the next stage which is valid
  const nextStage = getStagesInOrder(
    availableProgressStages.filter(
      (stage) => stage.id !== currentStage[0].id && !stage.completed && stage.isEligible && !stage.skipped
    )
  )[0];

  async function handleNext() {
    //Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === currentStage[0].id
          ? {
              ...stage,
              completed: true,
              viewActive: false,
              showProgress: true,
              skipped: false,
            }
          : stage;
      stage =
        stage.id === nextStage.id
          ? {
              ...stage,
              completed: false,
              viewActive: true,
              showProgress: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
    navigate(getNextView(nextStage.id));
  }

  async function goToClaimsOverview() {
    if (partnerId?.toUpperCase() === PartnerId.AFICS) {
      setModalLoadingOpen(true);
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_POST_FNOL_GO_TO_CLAIMS,
        undefined,
        undefined,
        claimSummary?.policyNumber
      );
    } else if (opco === OpCo.HOMESITE || opco === OpCo.AMFAM) {
      navigate('/claims-overview', {
        state: {
          isClaimRefreshNeeded: true,
        },
      });
    } else {
      navigate('/claim-detail', {
        state: {
          isClaimDetailRefreshNeeded: true,
        },
      });
    }
  }

  const headerContent = "You've selected " + provider.providerName;
  const servicesContent = (
    <>
      <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <ConfirmationHeader headerContent={headerContent} state={IconStateType.SUCCESS_STATE}></ConfirmationHeader>
        </Grid>

        <Grid sx={{ paddingTop: '2em', paddingBottom: '1rem' }} item xs={12}>
          <Typography sx={theme.custom.styles.confirmationSubHeader}>Details</Typography>
        </Grid>

        <Grid item xs={12}>
          <ContactCard isAuto={false} providerInfo={provider}></ContactCard>
        </Grid>
        <Grid item xs={12}>
          <Typography sx={theme.custom.styles.serviceConfirmationHeader}>Important Things to Know</Typography>
        </Grid>

        <Grid item xs={12}>
          <List sx={theme.custom.styles.confirmationList}>
            <ListItem sx={theme.custom.styles.confirmationListElement}>
              <Typography variant="body1">
                <b>
                  {provider.providerName} or {provider.providerName}'s contractor will contact you within 1 hour.{' '}
                </b>
                If you do not receive a call within 1 hour please call {getPrettyPhoneNumber(provider.providerPhone)}.
              </Typography>
            </ListItem>
            <ListItem sx={theme.custom.styles.confirmationListElement}>
              <Typography variant="body1">
                <b>
                  {provider.providerName} or {provider.providerName}'s contractor will arrive within 4 hours.
                </b>{' '}
                They will be properly identified and employees exhibit professional appearance.
              </Typography>
            </ListItem>

            <ListItem sx={theme.custom.styles.confirmationListElement}>
              <Typography variant="body1">
                <b>You may need to pay your deductible</b> to {provider.providerName} or {provider.providerName}'s
                contractor at the time of service.
              </Typography>
            </ListItem>

            <ListItem sx={theme.custom.styles.confirmationListElement}>
              <Typography variant="body1">
                <b>Remember to keep any and all related paperwork and receipts.</b>
              </Typography>
            </ListItem>

            <ListItem sx={theme.custom.styles.confirmationListElement}>
              <Typography variant="body1">
                <b>You should do everything necessary to protect your property from further damage.</b>
              </Typography>
            </ListItem>
          </List>
        </Grid>

        <Grid sx={{ marginTop: '3em' }} item xs={12}>
          <CallToAction
            nextButton={true}
            nextButtonContent={continueBtn}
            handleNext={handleNext}
            backButton={false}
            skipButton={false}
            cancelButton={true}
            cancelButtonContent={goToClaimsBtn}
            handleCancel={goToClaimsOverview}
          ></CallToAction>
        </Grid>
      </Grid>
    </>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {servicesContent}
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
    </Box>
  );
}
